import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { KeyRound, Copy } from 'lucide-react';

// External portal (/portal/login): an admin gives the participant, a family member or a guardian a login. They get an
// email to create their password; the admin can re-send it, disable / enable the account, or revoke access for good.
export const PORTAL_ROLES = [
  { value: 'PARTICIPANT', label: 'Participant (themselves)' },
  { value: 'FAMILY', label: 'Family member' },
  { value: 'GUARDIAN', label: 'Guardian' },
];
const STATUS_TONE = { INVITED: 'secondary', ACTIVE: 'default', DISABLED: 'outline', REVOKED: 'destructive' };
const EMPTY = { email: '', first_name: '', last_name: '', role: 'PARTICIPANT' };

const errorMessage = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return (d && typeof d === 'object' ? d.message : d) || fallback;
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

export function PortalAccessTab({ participantId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [lastLink, setLastLink] = useState(null);   // shown when the email could not be sent, so the admin can share it

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/participants/${participantId}/portal-access`)
      .then((r) => setRows(r.data?.data || []))
      .catch((e) => toast.error(errorMessage(e, 'Failed to load portal access')))
      .finally(() => setLoading(false));
  }, [participantId]);
  useEffect(() => { load(); }, [load]);

  const afterInvite = (data, okText) => {
    if (data?.email_sent === false) {
      setLastLink(data.invite_url);
      toast.warning('The invite email could not be sent — copy the link below and send it yourself');
    } else {
      setLastLink(null);
      toast.success(okText);
    }
  };

  const give = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post(`/participants/${participantId}/portal-access`, form);
      afterInvite(r.data?.data, `Invite sent to ${form.email.trim().toLowerCase()}`);
      setOpen(false); setForm(EMPTY); load();
    } catch (err) {
      toast.error(errorMessage(err, 'Failed to give portal access'));
    } finally { setSaving(false); }
  };

  const act = async (row, action, confirmText, okText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    try {
      const r = await api.post(`/participants/${participantId}/portal-access/${row.id}/${action}`);
      if (action === 'resend') afterInvite(r.data?.data, `Invite sent again to ${row.email}`);
      else toast.success(okText);
      load();
    } catch (err) {
      toast.error(errorMessage(err, 'Action failed'));
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(lastLink); toast.success('Link copied'); } catch { toast.error('Copy failed — select the link and copy it'); }
  };

  return (
    <Card data-testid="portal-access-tab">
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2"><KeyRound className="h-4 w-4" />Portal access</CardTitle>
          <CardDescription>Who can sign in to the participant portal for this participant.</CardDescription>
        </div>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="portal-access-give">Give portal access</Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {lastLink && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs space-y-2" data-testid="portal-access-link">
            <p>The invite email could not be sent. Share this link with the person (it works once, for 72 hours):</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate">{lastLink}</code>
              <Button size="sm" variant="outline" onClick={copy}><Copy className="h-3.5 w-3.5 mr-1" />Copy</Button>
            </div>
          </div>
        )}
        {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center" data-testid="portal-access-empty">No one has portal access yet.</p>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead>
              <TableHead>Status</TableHead><TableHead>Last sign-in</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`portal-access-row-${r.id}`}>
                  <TableCell>{[r.first_name, r.last_name].filter(Boolean).join(' ')}</TableCell>
                  <TableCell className="text-xs">{r.email}</TableCell>
                  <TableCell className="text-xs">{PORTAL_ROLES.find((x) => x.value === r.role)?.label || r.role}</TableCell>
                  <TableCell><Badge variant={STATUS_TONE[r.status] || 'outline'}>{r.status}</Badge></TableCell>
                  <TableCell className="text-xs">{fmt(r.last_login_at)}</TableCell>
                  <TableCell className="text-right space-x-1 whitespace-nowrap">
                    {r.status === 'INVITED' && <Button size="sm" variant="outline" onClick={() => act(r, 'resend')} data-testid={`portal-access-resend-${r.id}`}>Resend invite</Button>}
                    {(r.status === 'ACTIVE' || r.status === 'INVITED') && (
                      <Button size="sm" variant="outline" onClick={() => act(r, 'disable', `Disable portal access for ${r.email}? They cannot sign in until you enable it again.`, 'Access disabled')} data-testid={`portal-access-disable-${r.id}`}>Disable</Button>
                    )}
                    {r.status === 'DISABLED' && <Button size="sm" variant="outline" onClick={() => act(r, 'enable', null, 'Access enabled')} data-testid={`portal-access-enable-${r.id}`}>Enable</Button>}
                    {r.status !== 'REVOKED' && (
                      <Button size="sm" variant="destructive" onClick={() => act(r, 'revoke', `Revoke portal access for ${r.email}? This ends the account for good.`, 'Access revoked')} data-testid={`portal-access-revoke-${r.id}`}>Revoke</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={(o) => { if (!o) { setOpen(false); setForm(EMPTY); } }}>
        <DialogContent className="max-w-md" data-testid="portal-access-dialog">
          <form onSubmit={give}>
            <DialogHeader>
              <DialogTitle>Give portal access</DialogTitle>
              <DialogDescription>They get an email with a link to create their password and sign in at the participant portal.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label htmlFor="pa-first">First name *</Label>
                  <Input id="pa-first" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required /></div>
                <div className="space-y-1"><Label htmlFor="pa-last">Last name</Label>
                  <Input id="pa-last" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
              </div>
              <div className="space-y-1"><Label htmlFor="pa-email">Email *</Label>
                <Input id="pa-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
              <div className="space-y-1"><Label htmlFor="pa-role">Access for</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger id="pa-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{PORTAL_ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="portal-access-submit">{saving ? 'Sending…' : 'Send invite'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default PortalAccessTab;
